import { Permission, hasPermission } from "./permissions";
import { isMasterAdmin } from "../utils/isMasterAdmin";
import type { UserLoggedInterface } from "../interfaces/User.interface";
import type { ScreenId } from "../docs/screens.generated";

/**
 * What a screen requires to be shown or reached: one `Permission`, or a list
 * of which holding any one is enough. The list form exists for the
 * Organizations screen, which is both the organization-management screen and
 * the setup wizard's entry point (see `organizations` below).
 */
export type ScreenRequirement = Permission | readonly Permission[];

/**
 * The single declaration of what each screen requires -- read by BOTH the
 * sidebar link that offers a screen in Layout.tsx and that screen's own
 * page-level guard, via `useScreenPermission` (see
 * `customHooks/useScopedPermission.ts`). Before this map, the two were
 * separately hand-written `hasPermission(...)` calls that could silently name
 * different permissions for the same screen -- exactly how the Users sidebar
 * link and `UsersPage` disagreed.
 *
 * Keys are `ScreenId`, generated by `scripts/docsgen.mjs` from Layout.tsx's
 * own `<Route>` table and enforced by gate G-ROUTE
 * (`src/docs/__tests__/documentation.test.ts`) -- a typo'd key is a `tsc`
 * error, and a route with no `ScreenId` fails that gate.
 *
 * This map grows one entry at a time, in lockstep with the call sites that
 * are migrated to read from it -- an undeclared screen falls through to the
 * always-true sentinel below, matching a bare `hasPermission` call today, so
 * partial coverage changes no behaviour for the screens not yet listed here.
 */
export const SCREEN_PERMISSION: Partial<Record<ScreenId, ScreenRequirement>> = {
  /** The motivating bug: the sidebar checked Permission.Users, UsersPage
   *  checked Permission.ViewOrganizationManagementUsers. Declared once. */
  users: Permission.ViewOrganizationManagementUsers,

  /**
   * Replaces MonitoringPage's old bare `Permission.Organizations` guard (the
   * organization-management page key -- an accident of history, nothing to do
   * with monitoring) and the sidebar link's "always visible" rule. The calls
   * the page makes still need their own grants; the Roles editor's Monitoring
   * Dashboard switch turns them all on together (see permissionBundles.ts).
   */
  monitoring: Permission.ViewMonitoringDashboard,

  /**
   * The Organizations table is also where the setup wizard is opened from
   * ("Edit in wizard", "Edit my organization"), so a wizard installer who may
   * not otherwise manage organizations still reaches it.
   */
  organizations: [
    Permission.ViewOrganizationManagementOrganizations,
    Permission.WizardInstallerAccess,
  ],

  /** The wizard itself. It had no guard before: a direct URL visit reached it. */
  "setup-wizard": Permission.WizardInstallerAccess,
  roles: Permission.ViewOrganizationManagementRoles,
  /** API clients (LIFT-1098): listing them is what externals-view grants. */
  "api-clients": Permission.ExternalsView,

  /**
   * Reuses organizations' permission, matching the sidebar's pre-existing
   * value exactly -- not a new decision. Originally (through the
   * SCREEN_PERMISSION migration) OrganizationDraftsPage had no page-level
   * guard of its own -- the sidebar hiding the link was the only
   * enforcement, so a direct URL visit bypassed it for any authenticated
   * user regardless of permission. That gap is now closed:
   * OrganizationDraftsPage reads this same entry via `useScreenPermission`,
   * matching organizations/roles/users.
   */
  drafts: Permission.ViewOrganizationManagementOrganizations,

  adapters: Permission.ViewEdgeManagementAdapters,
  /** ScreenId is "map-view" (hyphenated, from the route path) -- the antd
   *  menu key "mapView" is a separate, unrelated string. */
  "map-view": Permission.ViewEdgeManagementMap,
  servers: Permission.ViewEdgeManagementServers,

  // Reports -- General (first of five Reports submenus migrated).
  "general-report-elevator-availability": Permission.ViewReportManagementGeneralCurrentOutOfService,
  "general-report-escalator-availability": Permission.ViewReportManagementGeneralEscalatorInUse,
  "general-report-performance": Permission.ViewReportManagementGeneralPerformance,
  "general-report-beneficial-usage": Permission.ViewReportManagementGeneralBeneficialUsage,
  "general-report-fire-service": Permission.ViewReportManagementGeneralFireService,

  // Reports -- Faults (second of five; Traffic, Car Use, Car Service follow).
  "faults-report-recent": Permission.ViewReportManagementFaultsRecentFaults,
  "faults-report-recent-escmov": Permission.ViewReportManagementFaultsRecentFaultsEscmov,
  "faults-report-log": Permission.ViewReportManagementFaultsLogByBank,
  "faults-report-car": Permission.ViewReportManagementFaultsFrequencyByCar,
  "faults-report-fault": Permission.ViewReportManagementFaultsFrequencyByFault,
  "faults-chart-lift": Permission.ViewReportManagementFaultsBycar,
  "faults-chart-lift-escmov": Permission.ViewReportManagementFaultsByCarEscmov,
  "faults-chart-floor": Permission.ViewReportManagementFaultsByFloor,
  "faults-chart-day": Permission.ViewReportManagementFaultsByDayWeek,
  "faults-chart-day-escmov": Permission.ViewReportManagementFaultsByDayWeekEscmov,
  "faults-chart-fault": Permission.ViewReportManagementFaultsByType,
  "faults-chart-fault-escmov": Permission.ViewReportManagementFaultsByTypeEscmov,

  // Reports -- Traffic (third of five; Car Use and Car Service follow).
  "traffic-report-detailed-summary": Permission.ViewReportManagementTrafficDetailedSummaryReport,
  "traffic-report-hall-call-summary": Permission.ViewReportManagementTrafficHallCallSummaryReport,
  "traffic-report-longest-wait-time-list":
    Permission.ViewReportManagementTrafficLongestWaitTimesList,
  "traffic-chart-trips-to-and-from-a-floor":
    Permission.ViewReportManagementTrafficTripsToAndFromFloor,
  "traffic-chart-hall-button-counts-by-floor":
    Permission.ViewReportManagementTrafficHallButtonCountsByFloor,
  "traffic-chart-hall-button-counts-by-time-of-day":
    Permission.ViewReportManagementTrafficHallButtonCountByTimeOfDay,
  "traffic-chart-average-by-floor": Permission.ViewReportManagementTrafficAverageByFloor,
  "traffic-chart-average-by-time-of-day": Permission.ViewReportManagementTrafficAverageByTimeOfDay,
  "traffic-chart-distribution-by-hourly-interval":
    Permission.ViewReportManagementTrafficDistributionByHourlyInterval,
  "traffic-chart-distribution-detailed-total":
    Permission.ViewReportManagementTrafficDistributionDetailedTotal,
  /** Permission member name itself has a pre-existing typo ("Detaile", not
   *  "Detailed") -- preserved verbatim, not introduced by this migration. */
  "traffic-chart-distribution-detailed-direction":
    Permission.ViewReportManagementTrafficDistributionDetaileDirection,

  // Reports -- Car Use (fourth of five).
  "car-use-chart-by-hour": Permission.ViewReportManagementCarUseByHour,
  "car-use-chart-comparisons": Permission.ViewReportManagementCarUseComparisons,
  "car-use-chart-times": Permission.ViewReportManagementCarUseDoorTimes,

  /**
   * Reports -- Car Service (fifth and last of the Reports submenus). Only
   * the 3 screens with a sidebar linkItem are declared here.
   * "car-service-chart-service-log" (ReportCarServiceChartServiceLog.tsx,
   * its own distinct permission ViewReportManagementCarServiceServiceLogChart)
   * has a <Route> but no sidebar entry -- reached only by drilling into the
   * Service Log report page -- so it's out of scope for this map, same as
   * every other no-sidebar-linkItem route throughout this migration.
   */
  "car-service-report-service-log": Permission.ViewReportManagementCarServiceServiceLogTable,
  "car-service-report-log": Permission.ViewReportManagementCarServiceLog,
  "car-service-report-statistics": Permission.ViewReportManagementCarServiceStatistics,

  /**
   * The 9 CRUD Route-element gates. Architecturally distinct from every
   * screen above: there is no sidebar linkItem and no separate page-level
   * guard to keep in sync -- the ONLY call site is the <Route> element's own
   * ternary in Layout.tsx, so there was never a "sidebar says X, page says
   * Y" drift risk here. What this migration changes for these 9 is that
   * their ternaries move from a bare `hasPermission(...)` to
   * `isScreenAllowed(...)`, which ADDS a MASTER ADMIN bypass that did not
   * exist on any of them before -- consistent with every other bypass this
   * migration has added (Layout.tsx's section toggles in prior PRs), not a
   * new decision.
   */
  "users-create": Permission.UsersCreate,
  "users-edit-id": Permission.UsersUpdate,
  "roles-create": Permission.RolesCreate,
  "roles-edit-id": Permission.RolesUpdate,
  // Read-only view -- gated by RolesView, not RolesUpdate: the action offered
  // for a master-organization role to users who may look but not edit.
  "roles-view-id": Permission.RolesView,
  /**
   * Pre-existing quirk, preserved verbatim: this route gates on
   * Permission.ServersView, not Permission.ServersCreate (which exists as
   * its own enum member and is unused here). Not introduced or corrected by
   * this migration -- flagged in the PR body, not silently changed either
   * direction.
   */
  "servers-create": Permission.ServersView,
  "adapters-create": Permission.AdaptersCreate,
  "adapters-edit-adapterid": Permission.AdaptersUpdate,
  // Duplicate reuses the Create permission -- duplicating produces a new
  // adapter record, matching adapters-create's requirement exactly.
  "adapters-duplicate-adapterid": Permission.AdaptersCreate,
};

/** The requirement for a screen, or the always-true sentinel if undeclared. */
export function screenRequirement(id: ScreenId): ScreenRequirement {
  return SCREEN_PERMISSION[id] ?? Permission.PermissionNotDefined;
}

/**
 * Pure set check against a screen's requirement -- no store access, no
 * MASTER ADMIN awareness.
 *
 * Use this from code that already has a `Set<string>` in hand and no access
 * to the store (e.g. Layout.tsx's menu builder, which runs inside a
 * `useMemo` and cannot call the `useScreenPermission` hook there). Prefer
 * `isScreenAllowed` (below) even from non-hook call sites, since it also
 * honours MASTER ADMIN -- reach for this one only when you already know the
 * asker is master admin by some other path.
 */
export function screenPermissionGranted(id: ScreenId, permissionSet: string[]): boolean {
  const requirement = screenRequirement(id);
  const anyOf: readonly Permission[] =
    typeof requirement === "string" ? [requirement] : requirement;
  return anyOf.some((permission) => hasPermission(permission, permissionSet));
}

/**
 * The single "is this screen allowed" decision -- MASTER ADMIN bypass plus
 * the declared requirement -- shared by both call shapes this codebase needs:
 * `useScreenPermission` (a hook, for components) and Layout.tsx's sidebar
 * menu builder (a plain call inside a `useMemo`, which cannot use hooks).
 * Centralising the OR here means a future change to the bypass rule is one
 * edit, not one per call site -- the exact class of drift this map exists to
 * prevent, moved up a level from "which Permission" to "was MASTER ADMIN
 * remembered".
 */
export function isScreenAllowed(
  id: ScreenId,
  userLogged: UserLoggedInterface | null | undefined,
  permissionSet: string[],
): boolean {
  return isMasterAdmin(userLogged) || screenPermissionGranted(id, permissionSet);
}
